import _ from "lodash";
import { updateTemplate } from "IBPlanner/modules/IBPlannerModule";
import { getPlannerFieldOptions } from "UnitPlans/modules/UnitPlanModule";
import { getLabelWithArchivedText } from "Utils";

export const isSubjectStandard = ({ elementType }) => {
  return _.includes(
    [
      "DP_SUBJECT_STANDARD",
      "PYP_LEARNING_STANDARD",
      "UBD_LEARNING_STANDARD",
      "MYP_LEARNING_STANDARD",
    ],
    elementType
  );
};

const getCombinedCourseOverview = ({ templates }) => {
  const combinedCourseOverview = [],
    uniqueElementTypes = [];

  _.forEach(templates, template => {
    const body = template?.body;
    const courseOverview = _.get(body, "courseOverview", []);

    if (courseOverview) {
      const fieldList = _.get(body, "field_list", {});
      _.forEach(courseOverview, field => {
        if (!_.find(combinedCourseOverview, { fieldUID: field?.fieldUID })) {
          const fieldItem = _.get(fieldList, field?.fieldUID);

          // we will later need element type to combine fields
          const elementType = _.get(
            fieldItem,
            "config.plannerElementConfig.type"
          );

          const isSubjectSpecificDynamic = _.get(
            fieldItem,
            "config.isSubjectSpecificDynamic",
            false
          );

          const updatedField = {
            ...field,
            elementType,
            isSubjectSpecificDynamic,
            shouldDisplay: !isSubjectSpecificDynamic,
          };

          if (!_.includes(uniqueElementTypes, elementType))
            uniqueElementTypes.push(elementType);
          combinedCourseOverview.push(updatedField);
        }
      });
    }
  });

  // take care of interdisciplinary ones that do not display on their own
  _.forEach(
    _.filter(combinedCourseOverview, ["isSubjectSpecificDynamic", true]),
    interdisciplinaryField => {
      const subjectSpecificField = _.find(
        combinedCourseOverview,
        field =>
          !_.isEqual(field?.fieldUID, interdisciplinaryField?.fieldUID) &&
          _.isEqual(field?.elementType, interdisciplinaryField?.elementType)
      );
      if (subjectSpecificField) {
        if (!_.has(subjectSpecificField, "config"))
          subjectSpecificField["config"] = {};

        subjectSpecificField["config"]["interdisciplinaryUID"] =
          interdisciplinaryField?.fieldUID;
      }
    }
  );

  return combinedCourseOverview;
};

const getPlannerFieldForCourseOverview = ({
  gradeTemplatesMap,
  unitPlanTemplates,
  subjects,
  subjectType,
  curriculumType,
  t,
}) => {
  const updatedTemplates = _.map(unitPlanTemplates, template => {
    const unitType = template?.unitType;
    const updatedTemplate = updateTemplate({
      t,
      template,
      curriculumType,
      unitType,
      resolvedSubjectNodes: subjects,
    });

    return updatedTemplate;
  });

  // to fetch labels of field headers, we'll check every template
  const templateFieldLists = _.map(updatedTemplates, "body.field_list");

  // use current grade's templates to generate course overview fields
  const gradeTemplates = _.map(gradeTemplatesMap, template =>
    _.find(updatedTemplates, { id: template.id })
  );

  const courseOverview = getCombinedCourseOverview({
    templates: gradeTemplates,
  });

  if (courseOverview) {
    const courseOverviewFields = _.reduce(
      courseOverview,
      (result, item) => {
        const fieldUID = item?.fieldUID;
        const gradeFieldObj = _.find(templateFieldLists, fieldList =>
          _.has(fieldList, fieldUID)
        )?.[fieldUID];

        // if current subject type doesn't have this field
        // or no templates in this grade has this field then skip
        if (
          (_.has(item, "subjectsToSkip") &&
            _.includes(item.subjectsToSkip, subjectType)) ||
          _.isEmpty(gradeFieldObj)
        )
          return result;

        // store fieldObjects from each unitPlan template
        // this will be used when clicking on a unit plan field edit button
        const fieldObjects = _.reduce(
          updatedTemplates,
          (result, template) => {
            const body = template?.body;
            if (body) {
              const interdisciplinaryUID = _.get(
                item,
                "config.interdisciplinaryUID"
              );

              if (_.has(body, ["field_list", fieldUID])) {
                result[template.id] = _.get(body, ["field_list", fieldUID]);
              }
              // MYP interdisciplinary fields are displayed under subject specific fields
              else if (_.has(body, ["field_list", interdisciplinaryUID])) {
                result[template.id] = _.get(body, [
                  "field_list",
                  interdisciplinaryUID,
                ]);
              }
            }

            return result;
          },
          {}
        );
        const fieldLabel = _.get(gradeFieldObj, "config.label", "");
        const elementType = _.get(
          item,
          "elementType",
          _.get(gradeFieldObj, "config.plannerElementConfig.type")
        );

        const itemConfig = _.get(item, "config", {});

        const courseOverviewField = {
          ...item,
          ...itemConfig,
          gradeFieldObj,
          fieldObjects,
          label: fieldLabel,
          elementType,
        };

        // incase the field has interdisciplinary counterparts that change with subject
        // push them into courseOverviewFields as well but don't display them, only used for fetching fieldObj
        if (_.has(itemConfig, "interdisciplinaryUID")) {
          const interdisciplinaryUID = itemConfig.interdisciplinaryUID;
          const interdisciplinaryTemplates = _.filter(updatedTemplates, [
            "unitType",
            "ibMYPInterdisciplinary",
          ]);

          _.forEach(subjects, subject => {
            const interdisciplinaryFieldObjects = _.reduce(
              interdisciplinaryTemplates,
              (result, template) => {
                const body = template?.body;
                if (body && _.has(body, ["field_list", interdisciplinaryUID]))
                  result[template.id] = _.get(body, [
                    "field_list",
                    interdisciplinaryUID,
                  ]);

                return result;
              },
              {}
            );
            result.push({
              // interdisciplinary fields are only displayed under their respective disciplinary field
              shouldDisplay: false,
              // a new field id is stored in unit plan for each subject's elements
              fieldUID: `${interdisciplinaryUID}_${subject.id}`,
              actualUID: fieldUID,
              fieldObjects: interdisciplinaryFieldObjects,
              label: fieldLabel,
              elementType,
            });
          });

          courseOverviewField["interdisciplinaryFields"] = _.map(
            subjects,
            subject => `${interdisciplinaryUID}_${subject.id}`
          );
        }
        result.push(courseOverviewField);

        return result;
      },
      []
    );

    return {
      courseOverviewFields,
      fieldUIDs: getDefaultFieldUIDs({
        courseOverviewFields,
        excludeHidden: false,
      }),
    };
  }

  return { courseOverviewFields: [], fieldUIDs: [] };
};

export const getPlannerFieldForCourseOverviewMemoized = _.memoize(
  params => getPlannerFieldForCourseOverview(params),
  props => JSON.stringify(props)
);

const getDefaultFieldUIDs = ({
  courseOverviewFields,
  excludeHidden = true,
}) => {
  if (!excludeHidden) {
    return [
      ..._.map(courseOverviewFields, "fieldUID"),
      "hours",
      "subjectLevel",
      "subjects",
      "subjectGroup",
      "objectiveLevel",
      "unitType",
      "theme",
    ];
  }

  const fieldUIDs = _.reduce(
    courseOverviewFields,
    (result, field) => {
      if (!_.get(field, "hideByDefault", false)) {
        result.push(field.fieldUID);
      }
      return result;
    },
    []
  );

  return fieldUIDs;
};

export const getDefaultFieldUIDsMemoized = _.memoize(
  params => getDefaultFieldUIDs(params),
  props => JSON.stringify(props)
);

const getFieldOptions = props => {
  const objectiveLevelNodes = _.get(
    _.find(props?.plannerElements, { type: "MYP_OBJECTIVES" }),
    "nodes",
    []
  );

  return getPlannerFieldOptions({ ...props, objectiveLevelNodes });
};

export const getFieldOptionsMemoized = _.memoize(
  params => getFieldOptions(params),
  props => JSON.stringify(props)
);

const getNodesFromPlannerSets = ({ plannerElements, type }) => {
  return _.get(_.find(plannerElements, { type }), "nodes", []);
};

export const getNodesFromPlannerSetsMemoized = _.memoize(
  params => getNodesFromPlannerSets(params),
  props => JSON.stringify(props)
);

const getSubjectStandardColumns = ({ subjectStandardField, subjects, t }) => {
  const fieldUID = subjectStandardField?.fieldUID;

  const isSelected = !_.get(subjectStandardField, "hideByDefault", false);
  const label = _.get(subjectStandardField, "label", "");

  return _.map(subjects, subject => {
    // turn off archived subjects by default
    const isArchived = _.get(subject, "isArchived", false);

    // store subjectId to filter out columns later based on that
    const subjectId = subject?.id;
    return {
      id: `${fieldUID}-${subjectId}`,
      subjectId,
      fieldUID,
      isSelected: isSelected && !isArchived,
      value: getLabelWithArchivedText({
        label: `${_.get(subject, "name", "")} ${_.lowerCase(label)}`,
        isArchived,
        t,
      }),
    };
  });
};

export const getColumnOptions = ({ courseOverviewFields, subjects, t }) => {
  return _.reduce(
    courseOverviewFields,
    (result, field) => {
      if (_.get(field, "shouldDisplay", true)) {
        const elementType = field?.elementType;

        // for subject standard, all columns are displayed separately
        if (
          isSubjectStandard({ elementType }) &&
          !_.isEqual(elementType, "DP_SUBJECT_STANDARD")
        ) {
          const subjectStandardsColumns = getSubjectStandardColumns({
            subjectStandardField: field,
            subjects,
            t,
          });

          result = _.concat(result, subjectStandardsColumns);
        } else {
          result.push({
            id: field?.fieldUID,
            isSelected: !_.get(field, "hideByDefault", false),
            value: _.get(field, "label", ""),
          });
        }
      }
      return result;
    },
    []
  );
};

const getNodesWithParentNodes = ({ filteredNodes, nodes }) => {
  return _.reduce(
    filteredNodes,
    (result, node) => {
      let iterator = node;

      while (iterator) {
        const isRoot = _.has(iterator, "parent") && _.isNull(iterator.parent);

        // don't push existing nodes
        if (!_.find(result, { id: iterator.id })) {
          result = [iterator, ...result];
        }

        // we've reached apex of the tree
        if (isRoot) break;

        iterator = _.find(nodes, { id: iterator.parent });
      }

      return result;
    },
    []
  );
};

export const getNodesWithParentNodesMemoized = _.memoize(
  params => getNodesWithParentNodes(params),
  props => JSON.stringify(props)
);

const getPYPYearlyInsightFilters = ({
  subjects,
  grades,
  curriculumProgramId,
}) => {
  return {
    subjects: _.map(subjects, "id"),
    grades,
    curriculumProgramId,
  };
};

export const getPYPYearlyInsightFiltersMemoized = _.memoize(
  params => getPYPYearlyInsightFilters(params),
  params => JSON.stringify(params)
);

const getTemplateFilters = ({ curriculumProgramId }) => {
  return {
    templateType: ["UNIT_PLAN"],
    curriculumProgramIds: curriculumProgramId ? [curriculumProgramId] : [],
  };
};

export const getTemplateFiltersMemoized = _.memoize(
  params => getTemplateFilters(params),
  params => JSON.stringify(params)
);

const getUpdatedFilter = ({ filters, courseId }) => {
  const updatedFilters = {
    ...filters,
    courses: [courseId],
    unitType: [],
  };
  return updatedFilters;
};

export const getUpdatedFilterMemoized = _.memoize(
  params => getUpdatedFilter(params),
  params => JSON.stringify(params)
);
